// Constantes y helpers de clasificación de leads.
//
// Los comparten el Kanban (LeadsKanbanBoard) y el tablero de Leads (LeadsBoardPage).
// Viven aquí y no duplicados en cada página: si el bucket de zonas se desincroniza,
// un lead deja de aparecer en el filtro de su zona y se pierde sin que nadie lo note.

/** Las 4 zonas canónicas. Mismas que REGION_ZONES del backend y las que escribe el clasificador. */
export const ZONE_COLUMNS = ['Cibao', 'Este', 'Sur', 'Santo Domingo', 'Sin zona'];

export const ZONE_COLORS: Record<string, string> = {
  'Cibao': '#2e7d32', 'Este': '#1565c0', 'Sur': '#e65100', 'Santo Domingo': '#6a1b9a', 'Sin zona': '#9e9e9e',
};

/** Nivel de interés que asigna el clasificador diario (backend/cron/leadClassifier.js). */
export const INTEREST_COLORS: Record<string, string> = { alto: '#2e7d32', medio: '#ed6c02', bajo: '#9e9e9e' };

export const STAGE_LABELS: Record<string, { label: string; color: any }> = {
  new: { label: 'Nuevo', color: 'info' },
  greeting: { label: 'Saludo', color: 'default' },
  qualifying: { label: 'Calificando', color: 'warning' },
  quoting: { label: 'Cotizando', color: 'primary' },
  closing: { label: 'Cerrando', color: 'success' },
  closed: { label: 'Ganado', color: 'success' },
  lost: { label: 'Perdido', color: 'error' },
};

/**
 * Bucket de zona a partir de clientZone/clientLocation.
 *
 * El clasificador escribe una de las 4 zonas canónicas en clientZone, pero quedan
 * conversaciones viejas donde sólo hay el municipio (o basura, como coordenadas GPS),
 * así que se mira también clientLocation.
 */
export function bucketZone(c: any): string {
  const t = `${c.clientZone || ''} ${c.clientLocation || ''}`.toLowerCase();
  if (/cibao|santiago|moca|\bvega\b|puerto plata|bonao|\bmao\b|navarrete|san francisco|\bsfm\b|cotui|valverde|salcedo|jarabacoa|constanza|espaillat|sosua|montecristi|dajabon|esperanza/.test(t)) return 'Cibao';
  if (/\beste\b|romana|higuey|higüey|bavaro|punta cana|san pedro|\bspm\b|hato mayor|\bseibo\b|monte plata|veron|bayaguana/.test(t)) return 'Este';
  if (/\bsur\b|san cristobal|\bbani\b|baní|\bazua\b|barahona|san juan|\bocoa\b|\bhaina\b|peravia|\bnizao\b|pedernales|neyba|jimani/.test(t)) return 'Sur';
  if (/santo domingo|distrito|capital|alcarrizos|villa mella|boca chica|los mina|san isidro|herrera|naco|piantini|gazcue|sabana perdida/.test(t)) return 'Santo Domingo';
  return 'Sin zona';
}

export function timeAgo(date: string | Date): string {
  if (!date) return '';
  const d = new Date(date).getTime();
  if (isNaN(d)) return '';
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const días = Math.floor(hrs / 24);
  return `hace ${días}d`;
}

/**
 * En qué ventana de Meta cae un lead. Son DOS ventanas distintas y se confunden fácil:
 *
 * - **Ventana de servicio — 24 h.** Abre con cada mensaje del cliente y cierra 24 h después
 *   del último. Mientras está abierta se le escribe texto libre y no cuesta nada. Cerrada,
 *   Meta rechaza el texto libre con el error 131047 ("Re-engagement message") y solo pasa
 *   una plantilla aprobada, que sí se cobra.
 * - **Ventana gratis de entrada — 72 h, hasta 7 días.** Solo para quien llegó por un anuncio
 *   click-to-WhatsApp y a quien le respondimos dentro de las primeras 24 h. Mientras dura,
 *   TODO es gratis, plantillas incluidas. Meta la extendió a 7 días el 28-sep-2026, pero por
 *   despliegue progresivo y sin cubrir los chats que entran por un botón de Página, así que
 *   de 72 h a 7 días se dice "probablemente gratis" y no se promete.
 *
 * Las 72 h son de **costo**, no de **permiso**: no habilitan texto libre. Por eso la ventana
 * de servicio sigue mandando sobre qué se puede enviar, y es un error subirla a 72 h —
 * los envíos entre 24 h y 72 h empiezan a fallar con 131047.
 */
export const VENTANA_SERVICIO_H = 24;
export const VENTANA_GRATIS_ADS_H = 72;
export const VENTANA_GRATIS_ADS_MAX_H = 168;

export type EstadoVentana = {
  /** Se le puede escribir texto libre (ventana de servicio abierta). */
  libre: boolean;
  /**
   * Si el dato alcanza para afirmarlo. La lista solo trae `lastMessageAt` —el último mensaje
   * de cualquiera—, y la ventana de Meta cuenta desde el último mensaje DEL CLIENTE. Cuando
   * la última palabra fue nuestra, el cliente escribió antes y la ventana está más cerrada de
   * lo que parece: se midió en el tablero y pasaba en 30 de las 36 filas que salían como
   * abiertas. Así que en ese caso no se promete nada; el dato exacto está al abrir el chat.
   */
  cierto: boolean;
  /** No cuesta: dentro de la de servicio, o dentro de las 72 h gratis por venir de un anuncio. */
  gratis: boolean;
  /** Fuera de las 24 h: hace falta plantilla aprobada. */
  requierePlantilla: boolean;
  /** Horas desde el último mensaje del cliente. */
  horas: number | null;
  /** Para la interfaz. */
  clave: 'libre' | 'gratis-ads' | 'plantilla' | 'incierto';
  etiqueta: string;
};

export function estadoVentana(c: any): EstadoVentana {
  const ref = c?.lastMessageAt;
  const d = ref ? new Date(ref).getTime() : NaN;
  const horas = isNaN(d) ? null : (Date.now() - d) / 3600000;

  // Si la última palabra fue nuestra, `horas` mide desde NUESTRO mensaje, no desde el del
  // cliente: la ventana real es igual o más corta. Pasadas las 24 h da igual (está cerrada
  // de todos modos); dentro, no se puede afirmar que esté abierta.
  const ultimaPalabraNuestra = !!c?.lastSender && c.lastSender !== 'client';

  if (horas === null || horas > VENTANA_SERVICIO_H) {
    // Fuera de la ventana de servicio. Si vino de un anuncio y no han pasado 72 h, la
    // plantilla no se cobra; pasadas, cuesta.
    const deAnuncio = !!(c?.adSourceId || c?.adCtwaClid);
    const gratisAds = deAnuncio && horas !== null && horas <= VENTANA_GRATIS_ADS_H;
    const quizaGratis = deAnuncio && horas !== null && horas > VENTANA_GRATIS_ADS_H && horas <= VENTANA_GRATIS_ADS_MAX_H;
    return {
      libre: false,
      cierto: true,
      gratis: gratisAds,
      requierePlantilla: true,
      horas,
      clave: gratisAds || quizaGratis ? 'gratis-ads' : 'plantilla',
      etiqueta: gratisAds
        ? 'plantilla gratis (anuncio)'
        : quizaGratis
          ? 'plantilla, gratis si aplican los 7 días'
          : 'plantilla con costo',
    };
  }
  if (ultimaPalabraNuestra) {
    return {
      libre: true,
      cierto: false,
      gratis: true,
      requierePlantilla: false,
      horas,
      clave: 'incierto',
      etiqueta: 'respondimos nosotros — abre el chat para ver la ventana',
    };
  }
  return { libre: true, cierto: true, gratis: true, requierePlantilla: false, horas, clave: 'libre', etiqueta: 'se puede escribir' };
}

/** Compatibilidad: lo que antes se llamaba "pasó de 24h" es hoy "requiere plantilla". */
export function isStale24h(lastMessageAt?: string | Date): boolean {
  return estadoVentana({ lastMessageAt }).requierePlantilla;
}

/** Texto corto para la fila del tablero. Vacío cuando no hay nada que advertir. */
export function avisoVentana(c: any): string {
  const v = estadoVentana(c);
  if (v.clave === 'libre') return '';
  if (v.clave === 'incierto') return ' · ventana por confirmar';
  return ` · ${v.etiqueta}`;
}

export function leadName(c: any): string {
  return c.leadName || c.contactName || c.phoneNumber || 'Sin nombre';
}
